import React, { useEffect, useRef } from "react";
import { RenderGrapesToHTML } from "./GrapesJSUtilities";
import { GenerateOldUiContainerStyle, renderOldUITemplate } from "./VisagePreview";
import { useTypedSelector } from "../../state/hooks";
import { GrapesDeviceTypes, GrapesRenderModes, IDisplayDeviceInfo, IVisageTemplate, VisageUIDisplayDevices } from "../../interfaces/VisageUI";
import { SimulationStates } from "../../globals";
import UITemplateTypes from "../uisystem/templates/types";

export default function VisageTemplatePreview(props: {
	templateId: string;
	wrapperRect: { width: number };
})
{
	const templateData = useTypedSelector<IVisageTemplate>(state => state.visageModule.uiTemplateData[props.templateId]);
	const quizTemplateData = useTypedSelector(state => state.uiModule.slideMaster[UITemplateTypes.QUIZ_RESULTS]?.[UITemplateTypes.QUIZ_RESULTS]);
	
	const [previewContainerStyle, setPreviewContainerStyle] = React.useState<React.CSSProperties>({});
	const [frameScale, setFrameScale] = React.useState(1);

	const frameRef = useRef<HTMLIFrameElement>(null);
	const previewContainerElement = useRef<HTMLDivElement>(null);

	useEffect(() =>
	{
		if(!frameRef.current || templateData.defaultTemplateID === UITemplateTypes.QUIZ_RESULTS) return;
		const doc = frameRef.current.contentDocument;
		if(!doc) return;
		
		const renderDocument = async () =>
		{
			doc.open();
			const html = await RenderGrapesToHTML(templateData.content, GrapesRenderModes.Preview);
			doc.write(html); // a bit slow. take another look at https://html2canvas.hertzen.com/ sometime
			doc.close();
		};

		renderDocument();
	}, [templateData, frameRef.current]);
	
	useEffect(() =>
	{
		const referenceWidth = 1920;
		const referenceHeight = 1080;

		const newContainerStyle: React.CSSProperties = {
			width: referenceWidth + "px",
			height: referenceHeight + "px",
			transformOrigin: "top left",
			transform: "",
			overflow: "hidden",
		};

		const scaleX = props.wrapperRect.width / referenceWidth;
		// const scaleY = props.wrapperRect.height / referenceHeight;
		// const scale = Math.min(scaleX, scaleY);
		newContainerStyle.transform = `scale(${scaleX})`;
		
		setFrameScale(scaleX);
		setPreviewContainerStyle({...newContainerStyle});
	}, [props.wrapperRect]);
	
	return (
		<div data-name={"visage-template-preview-root"}
			className={"visageTemplatePreviewRoot"}
			style={{
				height: (1080 * frameScale) + "px",
			}}
		>
			<div ref={previewContainerElement}
				data-name={"visage-template-preview-container"}
				style={previewContainerStyle}
			>
				{
					templateData && templateData.defaultTemplateID !== UITemplateTypes.QUIZ_RESULTS ?
						<iframe ref={frameRef}
							data-name={"visage-template-preview-frame"}
							className={"visagePreviewFrame"}
							style={{
								pointerEvents: "none",
							}}
						/>
						:
						quizTemplateData && <QuizResultsPreview templateData={quizTemplateData} />
				}
			</div>
		</div>
	)
}

export function QuizResultsPreview(props: {
	templateData: any,
	displayDevice?: IDisplayDeviceInfo,
	wrapperRect?: { width: number, height: number },
	["data-name"]?: string,
	displayWarnings?: boolean,
})
{
	const simulationAsViewer = useTypedSelector(state => state.applicationModule.simulationAsViewer);
	const previewScale = useTypedSelector(state => state.visageModule.uiPreviewScale);
	
	const displayDevice = props.displayDevice ?? VisageUIDisplayDevices.get(GrapesDeviceTypes.DesktopHD)!;
	const displayRect = props.wrapperRect ?? { width: displayDevice.resolution.width, height: displayDevice.resolution.height };

	return (
		<div className={"uiPreviewRoot"}
			data-name={props["data-name"] ? props["data-name"] : "old-ui-quiz-template-preview-wrapper"}
			data-status={"Normal"}
			style={GenerateOldUiContainerStyle({ width: displayRect.width, height: displayRect.height }, displayDevice, simulationAsViewer)}
		>
			{
				renderOldUITemplate(props.templateData, previewScale, SimulationStates.Stopped, UITemplateTypes.QUIZ_RESULTS, props.displayWarnings ?? false)
			}
		</div>
	);
}